import React, { useEffect, useState } from "react";
import Navbar from "../components/layout/navbar";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import useRequest from "../components/hooks/use-request";

interface ProfileData {
  username: string;
  email: string;
  phone: string;
  address: string;
  lastLogin: string;
  role: string;
}

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const Settings = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [editableProfile, setEditableProfile] = useState<ProfileData | null>(
    null
  );
  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isEditingProfile, setIsEditingProfile] = useState(false); // Track editing state

  // Get user token from localStorage or context
  const userToken = localStorage.getItem("token") || "";

  // Create API request hooks
  const { makeRequest: getProfileRequest } = useRequest(
    "/users/profile",
    "GET",
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  const { makeRequest: updateProfileRequest } = useRequest(
    "/users/profile",
    "PUT",
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  const { makeRequest: changePasswordRequest } = useRequest(
    "/auth/change-password/profile",
    "POST",
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  useEffect(() => {
    const fetchProfile = async () => {
      const [response, error] = await getProfileRequest();
      if (response) {
        setProfile(response.data?.user);
        setEditableProfile(response.data?.user);
      } else if (error) {
        setMessage({ type: "error", text: "Failed to fetch profile data" });
      }
    };
    fetchProfile();
  }, []);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editableProfile) return;

    setLoading(true);
    const [response, statusCode] = await updateProfileRequest(editableProfile);

    if (response) {
      setMessage({ type: "success", text: "Profile updated successfully" });
      setProfile(response.data?.user); // Update displayed profile
      setIsEditingProfile(false); // Exit editing mode
    } else {
      setMessage({
        type: "error",
        text: response?.message || "Failed to update profile",
      });
    }
    setLoading(false);
  };

  // Handle password change
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({
        type: "error",
        text: "Password must be at least 8 characters",
      });
      return;
    }

    setLoading(true);
    try {
      const [response] = await changePasswordRequest({
        currentPassword: passwordData.currentPassword,
        password: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });

      if (response.status) {
        setMessage({ type: "success", text: "Password updated successfully" });
        // Reset password fields
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        setMessage({
          type: "error",
          text: response.message || "Failed to update password",
        });
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setMessage({
        type: "error",
        text: "An error occurred while updating password",
      });
    } finally {
      setLoading(false);
    }
  };

  // Submit password change
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match" });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({
        type: "error",
        text: "Password must be at least 8 characters",
      });
      return;
    }

    setLoading(true);
    try {
      const [response] = await changePasswordRequest({
        currentPassword: passwordData.currentPassword,
        password: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });

      if (response.status) {
        setMessage({ type: "success", text: "Password updated successfully" });
        // Reset password fields
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        setMessage({
          type: "error",
          text: response.message || "Failed to update password",
        });
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setMessage({
        type: "error",
        text: "An error occurred while updating password",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar profile={profile as ProfileData} />

      <main className="flex-1 container mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
          <div className="mb-4 sm:mb-0">
            <h1 className="text-[30px] font-extrabold text-black">Settings</h1>
          </div>
        </div>

        {message.text && (
          <div
            className={`mb-4 p-3 rounded-md ${
              message.type === "error"
                ? "bg-red-100 text-red-700"
                : "bg-green-100 text-green-700"
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6">
          <div className="md:col-span-3">
            <Tabs defaultValue="profile" className="w-full">
              <TabsList className="w-full mb-6 justify-start">
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
              </TabsList>

              <TabsContent value="profile">
                <div className="flex flex-row items-start justify-between gap-4">
                  <div className="w-1/5">
                    <h4 className="text-black font-bold mb-2">Personal Info</h4>
                    <p className="text-sm">
                      You can change your personal information settings here.
                    </p>
                  </div>
                  <Card className="w-full">
                    <CardHeader className="flex flex-col items-start text-start space-y-2">
                      {/* <img
                        src={`https://ui-avatars.com/api/?name=${profile?.firstName}+${profile?.lastName}&background=0979A1&color=fff`}
                        alt="User Avatar"
                        className="w-16 h-16 rounded-full object-cover"
                      /> */}
                      <div>
                        <div className="flex items-center text-xs gap-3">
                          <h2 className="text-2xl font-bold text-gray-900">
                            {/* {profile?.firstName} {profile?.lastName} */}
                            {profile?.username}
                          </h2>
                          <p className="text-sm text-yellow-600 bg-yellow-50 rounded-2xl p-2">
                            {profile?.role}
                          </p>
                        </div>
                        <p className="text-sm text-gray-400">
                          {profile?.email}
                        </p>
                      </div>
                    </CardHeader>
                    <CardContent className="grid gap-6 py-4">
                      <form
                        onSubmit={handleProfileUpdate}
                        className="grid grid-cols-1 md:grid-cols-2 gap-4"
                      >
                        {/* First Name */}
                        {/* <div className="flex flex-col gap-1">
                          <label
                            htmlFor="firstName"
                            className="text-sm font-medium text-gray-700"
                          >
                            First Name
                          </label>
                          <input
                            id="firstName"
                            type="text"
                            value="John"
                            className="border rounded-2xl px-3 py-2 text-sm text-gray-800"
                          />
                        </div>

                        {/* Last Name 
                        <div className="flex flex-col gap-1">
                          <label
                            htmlFor="lastName"
                            className="text-sm font-medium text-gray-700"
                          >
                            Last Name
                          </label>
                          <input
                            id="lastName"
                            type="text"
                            value="Doe"
                            className="border rounded-2xl px-3 py-2 text-sm text-gray-800"
                          />
                        </div> */}

                        {/* Username */}
                        <div className="flex flex-col gap-1">
                          <label
                            htmlFor="username"
                            className="text-sm font-medium text-gray-700"
                          >
                            Username
                          </label>
                          <input
                            id="username"
                            type="text"
                            value={editableProfile?.username || ""}
                            // onChange={handleProfileInputChange}
                            className="border rounded-2xl px-3 py-2 text-sm text-gray-800"
                            required
                          />
                        </div>

                        {/* Email */}
                        <div className="flex flex-col gap-1">
                          <label
                            htmlFor="email"
                            className="text-sm font-medium text-gray-700"
                          >
                            Email
                          </label>
                          <input
                            id="email"
                            type="email"
                            value={editableProfile?.email || ""}
                            // onChange={handleProfileInputChange}
                            className="border rounded-2xl px-3 py-2 text-sm text-gray-800"
                            required
                          />
                        </div>

                        {/* Role */}
                        <div className="flex flex-col gap-1">
                          <label
                            htmlFor="role"
                            className="text-sm font-medium text-gray-700"
                          >
                            Role
                          </label>
                          <input
                            id="role"
                            type="text"
                            value={editableProfile?.role || ""}
                            className="border rounded-2xl px-3 py-2 text-sm text-gray-800"
                          />
                        </div>

                        {/* Phone Number */}
                        <div className="flex flex-col gap-1">
                          <label
                            htmlFor="phone"
                            className="text-sm font-medium text-gray-700"
                          >
                            Phone Number
                          </label>
                          <input
                            id="phone"
                            type="tel"
                            value={editableProfile?.phone || ""}
                            // onChange={handleProfileInputChange}
                            className="border rounded-2xl px-3 py-2 text-sm text-gray-800"
                          />
                        </div>
                        <div className="col-span-2 flex justify-end gap-4 my-4">
                          <Button className="rounded-2xl border p-2">
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="rounded-2xl bg-green-700 text-white border p-2"
                          >
                            Edit Profile
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="security">
                <div className="flex flex-row items-start justify-between gap-4">
                  {/* Sidebar Info */}
                  <div className="w-1/5">
                    <h4 className="text-black font-bold mb-2">Security</h4>
                    <p className="text-sm">
                      You can change your password settings here.
                    </p>
                  </div>

                  {/* Password Change Card */}
                  <div className="flex-1">
                    <Card>
                      <CardHeader>
                        <CardTitle>Change Password</CardTitle>
                      </CardHeader>

                      <CardContent className="space-y-6">
                        <form
                          onSubmit={handlePasswordSubmit}
                          className="space-y-4"
                        >
                          {/* Current Password */}
                          <div className="flex flex-col gap-1">
                            <label
                              htmlFor="currentPassword"
                              className="text-sm font-medium text-gray-700"
                            >
                              Current Password
                            </label>
                            <input
                              id="currentPassword"
                              type="password"
                              placeholder="Enter current password"
                              className="border rounded-2xl px-3 py-2 text-sm text-gray-800"
                              value={passwordData.currentPassword}
                              onChange={handlePasswordChange}
                              required
                            />
                          </div>

                          {/* New Password */}
                          <div className="flex flex-col gap-1">
                            <label
                              htmlFor="newPassword"
                              className="text-sm font-medium text-gray-700"
                            >
                              New Password
                            </label>
                            <input
                              id="newPassword"
                              type="password"
                              placeholder="Enter new password"
                              className="border rounded-2xl px-3 py-2 text-sm text-gray-800"
                              value={passwordData.newPassword}
                              onChange={handlePasswordChange}
                              required
                              minLength={8}
                            />
                          </div>

                          {/* Confirm New Password */}
                          <div className="flex flex-col gap-1">
                            <label
                              htmlFor="confirmPassword"
                              className="text-sm font-medium text-gray-700"
                            >
                              Confirm New Password
                            </label>
                            <input
                              id="confirmPassword"
                              type="password"
                              placeholder="Confirm new password"
                              className="border rounded-2xl px-3 py-2 text-sm text-gray-800"
                              value={passwordData.confirmPassword}
                              onChange={handlePasswordChange}
                              required
                            />
                          </div>

                          <div className="flex justify-end gap-4 mt-6">
                            <Button
                              type="button"
                              className="rounded-2xl border p-2"
                              onClick={() => {
                                setPasswordData({
                                  currentPassword: "",
                                  newPassword: "",
                                  confirmPassword: "",
                                });
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              className="rounded-2xl bg-green-700 text-white border p-2"
                              disabled={loading}
                            >
                              {loading ? "Updating..." : "Update Password"}
                            </Button>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
